import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { mixins } from "../styles";

@customElement("ptc-scroller")
export class Scroller extends LitElement {
    @property({ type: Boolean })
    locked = false;

    /** Reserve space for the scrollbar so content doesn't shift when it appears/disappears. */
    @property({ type: Boolean, reflect: true, attribute: "stable-gutter" })
    stableGutter = false;

    get scrollTop() {
        return this._content.scrollTop;
    }

    set scrollTop(val: number) {
        this._content.scrollTop = val;
    }

    get scrollLeft() {
        return this._content.scrollLeft;
    }

    set scrollLeft(val: number) {
        this._content.scrollLeft = val;
    }

    @query(".shadow.top")
    private _shadowTop: HTMLElement;

    @query(".shadow.bottom")
    private _shadowBottom: HTMLElement;

    @query(".content")
    private _content: HTMLElement;

    private _intersectionObserver = new IntersectionObserver(() => this._scheduleScroll());
    private _mutationObserver = new MutationObserver(() => this._scheduleScroll());

    connectedCallback() {
        super.connectedCallback();
        this._intersectionObserver.observe(this);
        this._mutationObserver.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._intersectionObserver.disconnect();
        this._mutationObserver.disconnect();
        cancelAnimationFrame(this._scrollRaf);
    }

    private _scrollRaf = 0;
    private _lastTopShadow = "";
    private _lastBottomShadow = "";

    // Coalesce the scroll event + both observers into one pre-paint rAF: _scroll reads layout then
    // writes boxShadow, so running it per call (many per frame) is a forced reflow each time.
    private _scheduleScroll = () => {
        if (this._scrollRaf) {
            return;
        }
        this._scrollRaf = requestAnimationFrame(() => {
            this._scrollRaf = 0;
            this._scroll();
        });
    };

    private _scroll() {
        const content = this._content;
        const x = Math.min(content.scrollTop / 30, 1);
        const y = Math.min((content.scrollHeight - content.offsetHeight - content.scrollTop) / 30, 1);
        const topShadow = `rgba(0, 0, 0, ${x * 0.3}) 0 1px ${x * 6}px -3px`;
        const bottomShadow = `rgba(0, 0, 0, ${y * 0.3}) 0 -1px ${y * 6}px -3px`;
        // Skip redundant writes so we don't dirty layout when the shadows haven't changed.
        if (topShadow !== this._lastTopShadow) {
            this._shadowTop.style.boxShadow = topShadow;
            this._lastTopShadow = topShadow;
        }
        if (bottomShadow !== this._lastBottomShadow) {
            this._shadowBottom.style.boxShadow = bottomShadow;
            this._lastBottomShadow = bottomShadow;
        }
        this.dispatchEvent(new CustomEvent("scroll"));
    }

    static styles = [
        css`
            :host {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                position: relative;
            }

            .content {
                flex: 1;
                overscroll-behavior: contain;
                scroll-behavior: smooth;
                scrollbar-width: thin;
            }

            .content:not(.locked) {
                ${mixins.scroll()};
            }

            :host([stable-gutter]) .content {
                scrollbar-gutter: stable;
            }

            ${mixins.customScrollbar(".content")}

            .shadow {
                position: absolute;
                left: 0;
                right: 0;
                height: 10px;
                z-index: 5;
            }

            .shadow.top {
                top: -10px;
            }

            .shadow.bottom {
                bottom: -10px;
            }

            :host([hide-scrollbar]) ::-webkit-scrollbar {
                display: none;
            }

            @media print {
                .shadow {
                    display: none;
                }
            }
        `,
    ];

    render() {
        return html`
            <div class="top shadow"></div>

            <div class="content ${this.locked ? "locked" : ""}" @scroll=${this._scheduleScroll}>
                <slot></slot>
            </div>

            <div class="bottom shadow"></div>
        `;
    }
}
